<template>
  <div id="payout_detail">
    <Navbar current="Detalhes do Repasse" />
    <Sidebar />
    <Container col="col-12">
      <Header
        :isSubHeader="false"
        title="Detalhes do Repasse"
        :iconClass="'fa-solid fa-money-bill-transfer text-primary'"
      >
        <template v-slot:button>
          <a
            v-if="stripePayoutLink()"
            :href="stripePayoutLink()"
            target="_blank"
            class="btn btn-outline-secondary"
          >
            <i class="fa-brands fa-stripe me-1"></i> Ver no Stripe
          </a>
        </template>
      </Header>

      <LoadingContainer v-if="loading" />

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else>
        <div class="row g-3 mb-3">
          <!-- Cabeçalho: id, status, valor, psi -->
          <div class="col-12 col-lg-4 d-flex">
            <div class="card flex-fill">
              <div class="card-body">
                <div class="d-flex flex-wrap gap-3">
                  <div>
                    <small class="text-muted d-block">Repasse</small>
                    <strong class="fs-5">#{{ payout.id }}</strong>
                  </div>
                  <div>
                    <small class="text-muted d-block">Status</small>
                    <span class="badge fs-6" :class="statusClass(payout.status)">
                      {{ payout.status_label }}
                    </span>
                  </div>
                  <div>
                    <small class="text-muted d-block">Valor</small>
                    <strong class="fs-5">{{ payout.valor.toCurrency() }}</strong>
                  </div>
                  <div>
                    <small class="text-muted d-block">Pago em</small>
                    <span>{{ formatDate(payout.data_pagamento) }}</span>
                  </div>
                </div>
                <hr />
                <small class="text-muted d-block">Psicólogo</small>
                <router-link
                  :to="`${$paths.psychologists.detail}/${psychologist.id}`"
                  target="_blank"
                >
                  {{ psychologist.name }}
                  <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                </router-link>
                <small class="text-muted d-block">{{ psychologist.email }}</small>

                <div v-if="payout.falha_mensagem" class="alert alert-danger mt-3 mb-0">
                  <strong>Falha:</strong> {{ payout.falha_mensagem }}
                  <span v-if="payout.falha_codigo" class="text-muted">
                    ({{ payout.falha_codigo }})
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Resumo / confere -->
          <div class="col-12 col-lg-4 d-flex">
            <div class="card flex-fill">
              <div class="card-header d-flex justify-content-between align-items-center">
                <strong>Resumo</strong>
                <span
                  class="badge"
                  :class="summary.confere ? 'bg-success' : 'bg-danger'"
                >
                  <i
                    class="fa-solid me-1"
                    :class="summary.confere ? 'fa-check' : 'fa-triangle-exclamation'"
                  ></i>
                  {{ summary.confere ? "Íntegro" : "Inconsistente" }}
                </span>
              </div>
              <div class="card-body">
                <div class="row g-3 text-center">
                  <div class="col-4">
                    <small class="text-muted d-block">Sessões</small>
                    <strong>{{ summary.qtd_sessoes }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Transações</small>
                    <strong>{{ summary.qtd_transacoes }}</strong>
                  </div>
                  <div class="col-4" v-if="!summary.confere">
                    <small class="text-muted d-block">Diferença</small>
                    <strong class="text-danger">{{ summary.diferenca.toCurrency() }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Total bruto</small>
                    <strong>{{ summary.total_bruto.toCurrency() }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Total taxas</small>
                    <strong>{{ summary.total_taxas.toCurrency() }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Total líquido</small>
                    <strong>{{ summary.total_liquido.toCurrency() }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Conta bancária -->
          <div class="col-12 col-lg-4 d-flex">
            <div class="card flex-fill">
              <div class="card-header"><strong>Conta bancária</strong></div>
              <div class="card-body">
                <div v-if="bankAccount">
                  <span>{{ bankAccount.bank_name || bankAccount.routing_number || "—" }}</span>
                  <span v-if="bankAccount.last4"> · final {{ bankAccount.last4 }}</span>
                </div>
                <div v-else class="text-muted">
                  Indisponível (conta antiga/removida na Stripe).
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Fundos relacionados (repasses que falharam antes/depois) -->
        <div
          v-if="fundsReceivedFrom.length || fundsReturnedTo.length"
          class="card mb-3"
        >
          <div class="card-header"><strong>Repasses relacionados</strong></div>
          <div class="card-body">
            <div v-if="fundsReceivedFrom.length" class="mb-3">
              <small class="text-muted d-block mb-1">
                Este repasse carregou dinheiro que voltou destes repasses falhos:
              </small>
              <ul class="list-unstyled mb-0">
                <li v-for="r in fundsReceivedFrom" :key="'in-' + r.id" class="mb-1">
                  <router-link :to="`${$paths.payouts.detail}/${r.id}`">
                    Repasse #{{ r.id }}
                  </router-link>
                  — {{ r.valor.toCurrency() }} ·
                  <span class="badge" :class="statusClass(r.status)">{{ r.status_label }}</span>
                  <span v-if="r.falha_motivo" class="text-muted"> — {{ r.falha_motivo }}</span>
                </li>
              </ul>
            </div>
            <div v-if="fundsReturnedTo.length">
              <small class="text-muted d-block mb-1">
                O dinheiro deste repasse (que falhou) saiu depois neste outro repasse:
              </small>
              <ul class="list-unstyled mb-0">
                <li v-for="r in fundsReturnedTo" :key="'out-' + r.id" class="mb-1">
                  <router-link :to="`${$paths.payouts.detail}/${r.id}`">
                    Repasse #{{ r.id }}
                  </router-link>
                  — {{ r.valor.toCurrency() }} ·
                  <span class="badge" :class="statusClass(r.status)">{{ r.status_label }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card">
          <ul class="nav nav-tabs px-3 pt-2" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link active"
                id="payout-sessions-tab"
                data-bs-toggle="tab"
                data-bs-target="#payout-sessions"
                type="button"
                role="tab"
              >
                Sessões pagas neste repasse
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                id="payout-transactions-tab"
                data-bs-toggle="tab"
                data-bs-target="#payout-transactions"
                type="button"
                role="tab"
              >
                Transações (detalhe técnico)
              </button>
            </li>
          </ul>
          <div class="tab-content">
            <!-- Sessões -->
            <div
              class="tab-pane fade show active"
              id="payout-sessions"
              role="tabpanel"
            >
              <div class="table-responsive">
                <table class="table table-hover table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Data sessão</th>
                      <th>Paciente</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th class="text-end">Valor da sessão</th>
                      <th class="text-end">Bruto</th>
                      <th class="text-end">Taxa</th>
                      <th class="text-end">Líquido</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in paidSessions" :key="s.sessao_id">
                      <td>
                        <router-link
                          :to="`${$paths.appointments.detail}/${s.sessao_id}`"
                          target="_blank"
                        >
                          {{ formatDate(s.data) }}
                        </router-link>
                      </td>
                      <td>
                        <router-link
                          v-if="s.paciente"
                          :to="`${$paths.users.detail}/${s.paciente.id}`"
                          target="_blank"
                        >
                          {{ s.paciente.nome }}
                        </router-link>
                        <span v-else class="text-muted">Conta removida</span>
                      </td>
                      <td>{{ sessionTypeLabel(s) }}</td>
                      <td>{{ s.status_sessao }}</td>
                      <td class="text-end">
                        <span v-if="s.valor_sessao != null">{{ s.valor_sessao.toCurrency() }}</span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-end">{{ s.valor_bruto.toCurrency() }}</td>
                      <td class="text-end">{{ s.taxa.toCurrency() }}</td>
                      <td class="text-end">{{ s.valor_liquido.toCurrency() }}</td>
                      <td class="text-end">
                        <span v-if="s.origem === 'relinkada'" class="badge bg-light text-muted border" :title="s.observacao">
                          relinkada
                        </span>
                        <router-link
                          :to="`${$paths.appointments.detail}/${s.sessao_id}`"
                          target="_blank"
                          class="btn btn-sm btn-white ms-1"
                        >
                          <span class="fe fe-arrow-up-right"></span>
                        </router-link>
                      </td>
                    </tr>
                    <tr v-if="!paidSessions.length">
                      <td colspan="9" class="text-center text-muted py-4">
                        Nenhuma sessão paga neste repasse.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Transações (detalhe técnico) -->
            <div
              class="tab-pane fade"
              id="payout-transactions"
              role="tabpanel"
            >
              <div class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Criado em</th>
                      <th>Tipo</th>
                      <th>Categoria</th>
                      <th class="text-end">Bruto</th>
                      <th class="text-end">Taxa</th>
                      <th class="text-end">Líquido</th>
                      <th>Status</th>
                      <th>Descrição</th>
                      <th>Sessão</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in transactions" :key="t.id">
                      <td>{{ formatDate(t.criado_em) }}</td>
                      <td>{{ typeLabel(t.tipo) }}</td>
                      <td>{{ categoryLabel(t.categoria) }}</td>
                      <td class="text-end">{{ t.valor_bruto.toCurrency() }}</td>
                      <td class="text-end">{{ t.taxa.toCurrency() }}</td>
                      <td class="text-end">{{ t.valor_liquido.toCurrency() }}</td>
                      <td>{{ transactionStatusLabel(t.status) }}</td>
                      <td class="text-muted small">{{ t.descricao || "-" }}</td>
                      <td>
                        <router-link
                          v-if="t.sessao_id"
                          :to="`${$paths.appointments.detail}/${t.sessao_id}`"
                          target="_blank"
                        >
                          <template v-if="sessionFor(t.sessao_id)">
                            {{ formatDate(sessionFor(t.sessao_id).data) }} ·
                            {{ sessionFor(t.sessao_id).paciente?.nome || "Conta removida" }}
                          </template>
                          <template v-else>#{{ t.sessao_id }}</template>
                          <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                        </router-link>
                        <span v-else class="text-muted">-</span>
                      </td>
                    </tr>
                    <tr v-if="!transactions.length">
                      <td colspan="9" class="text-center text-muted py-4">
                        Nenhuma transação neste repasse.
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="transactions.length">
                    <tr>
                      <th colspan="5" class="text-end">
                        Total líquido
                        <small class="text-muted d-block">(exclui a linha do próprio repasse)</small>
                      </th>
                      <th class="text-end">{{ transactionsNetTotal.toCurrency() }}</th>
                      <th colspan="3"></th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import PayoutDetailController from "./PayoutDetailController";
export default PayoutDetailController;
</script>
